/**
 * Доля хвоста публичного рендерера: вид `return_to_app_button` (кнопка
 * «вернуться в приложение» на экране после покупки).
 *
 * WEB-2920, заход 3: перенесено из `PublicBlockRendererExtended.tsx` слово в
 * слово вместе с компонентом кнопки (`ReturnToAppButton`). Метка
 * `case 'return_to_app_button'` остаётся в фасаде; ветка вида — функция
 * отрисовки `renderReturnToAppButton(ctx)`, стиль кнопки и ОБЩИЙ цвет текста
 * воронки (проп рендерера, без лестницы блока) приходят объектом-контекстом
 * (`ExtendedRenderContext`). Эту долю импортирует только фасад: она едет в
 * ленивом куске `extended`, а не в первом экране (самопроверка обхода).
 */
import React, { useState } from 'react';
import { buttonSurfaceStyle } from '../blockBackgroundStyle';
import type { EditorBlock } from '@/store/editorSlice';
import { interpolate } from '@/player/interpolate';
import type { VariableContext } from '@/player/variable-context';
import { RichTextContent } from '../RichTextContent';
import { asRichField } from '@/types/rich-text';
import { appLinkHref } from '@/utils/appLinkHref';
import { primaryButtonStyle } from '@/themes';
import type { ButtonStyle } from '@/themes/theme.types';
import { useEditorPreview } from '../EditorPreviewContext';
import { getPublicQuizStrings } from '@/utils/publicQuizStrings';
import { postToWeb2AppBridge } from '@/utils/web2appBridge';
import { openAppOrStore } from '@/utils/openAppOrStore';
import { usePostPurchaseContext } from '@/components/paywall-runtime/PostPurchaseContext';
import { pickStrings } from '@/i18n';
// WEB-2057: подсказки АВТОРУ — из своего маленького словаря. Полный словарь
// конструктора этому файлу нельзя: его рисует ПУБЛИЧНАЯ страница покупателя.
import { authorHintsStrings } from '@/i18n/dictionaries/authorHints';
import { AuthorHintBox } from '../AuthorHintBox';
import type { ExtendedRenderContext } from '../PublicBlockRendererExtended';

interface ReturnToAppButtonProps {
  block: EditorBlock;
  buttonStyle?: ButtonStyle;
  textColor?: string;
  varCtx?: VariableContext;
}

/**
 * WEB: return_to_app_button — the paywall post-purchase screen's "return to
 * the app" CTA. Its href/onClick come from PostPurchaseContext (appSchemeUrl
 * resolved by the success-data fetch, WEB-800) via usePostPurchaseContext(),
 * a hook — extracted from the switch below like DownsellPopup above, since
 * hooks cannot be called conditionally inside a switch statement in the
 * parent render function.
 *
 * Reuses button_to_pay's styling (primaryButtonStyle + rich buttonText) —
 * see PublicBlockRenderer.tsx's `case 'button_to_pay'`. Renders even with no
 * appSchemeUrl (no href) so the block stays visible on the editor canvas,
 * where no PostPurchaseContext.Provider wraps it.
 */
function ReturnToAppButton({
  block,
  buttonStyle,
  textColor,
  varCtx,
}: ReturnToAppButtonProps): React.ReactElement {
  const {
    appSchemeUrl,
    installUrlIos,
    installUrlAndroid,
    iosStoreUrl,
    androidStoreUrl,
  } = usePostPurchaseContext();
  // WEB-846: в превью редактора клик не навигирует (кнопка без target=_blank
  // уводила вкладку редактора со страницы) и не шлёт бридж-событие SDK.
  // Публичный путь ниже — отлаженный flow возврата в приложение, не менять.
  const { isEditorPreview } = useEditorPreview();
  // WEB-854: пока идёт попытка открыть приложение — текст человеку, чтобы
  // экран не выглядел мёртвым.
  const [attempting, setAttempting] = useState(false);
  // Своя ссылка автора (block.url) заменяет автоматическую схему возврата —
  // кейс «покупатель без приложения → App Store». Мост-событие close ниже
  // шлётся в любом случае, SDK-механизм от ссылки не зависит.
  //
  // WEB-1911: адрес чистится СВОЕЙ дверью (utils/appLinkHref) — общий
  // safeHref схлопнул бы саму схему приложения, ради которой ветку и делали в
  // обход. Негодный адрес (маркер `w2a://store`, `javascript:`, чужая схема)
  // = адреса нет: кнопка возвращается к штатной схеме возврата, а мёртвый
  // href покупателю не уезжает.
  const safeSchemeUrl = appLinkHref(appSchemeUrl, appSchemeUrl);
  const authoredHref = appLinkHref(block.url, appSchemeUrl);
  const href = authoredHref ?? safeSchemeUrl;
  // Подсказка АВТОРУ ровно в том случае, когда ссылку ЗАДАЛИ, а дверь её не
  // пропустила (чужая схема, маркер `w2a://store`, `javascript:`): кнопка
  // молча возвращается к штатной схеме возврата — без слов это выглядит как
  // «ссылка работает». Правило то же, что у соседней кнопки «Закрыть окно»
  // (ревизия пачки 19): «ссылка задана и не прошла». Ссылки нет вовсе —
  // подсказки нет: автор ничего не просил. Только на канве: покупателю
  // служебных плашек не носим.
  const showAuthorHint =
    isEditorPreview &&
    typeof block.url === 'string' &&
    block.url.trim().length > 0 &&
    !authoredHref;
  // Ревизия пачки 16, М-3: returnToAppOpeningHint было единственным из 4
  // системных полей WEB-1429 без интерполяции {{переменных}} — тот же приём,
  // что у RestorePurchasesLink.label ниже по файлу (interpolate только когда
  // varCtx есть, иначе строка как есть — на превью конструктора varCtx часто
  // не проброшен).
  const rawOpeningHint =
    (block.returnToAppOpeningHint ?? '').trim() ||
    getPublicQuizStrings(varCtx?.system?.locale).openingAppHint;
  const openingHint = varCtx
    ? interpolate(rawOpeningHint, varCtx)
    : rawOpeningHint;
  return (
    <div style={{ textAlign: 'center', marginBottom: 16 }}>
      <a
        data-testid={`return-to-app-button-${block.id}`}
        href={href}
        // WebView-режим SDK: сообщаем мосту о закрытии (см. paywall-close-to-app
        // на ThankYouView) — во внешнем браузере сработает обычный href-переход.
        onClick={(e) => {
          if (isEditorPreview) {
            e.preventDefault();
            // WEB-1722: на канвасе конструктора настоящей попытки открыть
            // приложение не бывает, поэтому подсказку «Открываем приложение…»
            // автор не видел НИКОГДА и настраивал её вслепую. Тот же жест,
            // которым её вызовет покупатель, включает и выключает её в
            // предпросмотре. Публичный путь ниже не тронут: он приходит сюда
            // только при isEditorPreview === false.
            setAttempting((shown) => !shown);
            return;
          }
          const posted = postToWeb2AppBridge({ event: 'close' });
          if (posted) return; // внутри контейнера закрывает SDK
          // WEB-854: во внешнем браузере переход по схеме молча ничего не
          // делает, когда приложения нет. Берём его на себя — попытка,
          // короткий таймер, магазин. Своя ссылка автора (block.url) —
          // обычный http(s)-переход, её механику не трогаем.
          //
          // WEB-1911: смотрим на ОЧИЩЕННЫЕ адреса. Негодная ссылка автора не
          // должна отбирать у покупателя запасной путь: раньше любой
          // непустой block.url (включая маркер) выходил отсюда, и клик не
          // делал ровно ничего.
          if (authoredHref || !safeSchemeUrl) return;
          e.preventDefault();
          setAttempting(true);
          const outcome = openAppOrStore({
            schemeUrl: safeSchemeUrl,
            installUrlIos,
            installUrlAndroid,
            iosStoreUrl,
            androidStoreUrl,
            onSettled: () => setAttempting(false),
          });
          if (outcome !== 'app-attempt') setAttempting(false);
        }}
        style={{
          display: 'inline-block',
          ...primaryButtonStyle(buttonStyle, textColor),
          width: '100%',
          textDecoration: 'none',
          // Запрос владельца продукта 10.08 + WEB-1651-2: «Фон кнопки» и цвет
          // текста из панели — перекрывают акцент темы ТОЛЬКО когда заданы
          // (переиспользует button_to_pay's хелпер так же, как строка выше
          // переиспользует primaryButtonStyle).
          ...buttonSurfaceStyle(block),
          // Дозаказ владельца продукта: та же px-настройка, что у
          // button_next/button_to_pay (часть 1) — сильнее дефолтного
          // fontSize из primaryButtonStyle.
          ...(block.fontSize ? { fontSize: block.fontSize } : {}),
        }}
      >
        <RichTextContent
          value={asRichField(block.buttonText)}
          varCtx={varCtx}
          fallback={
            getPublicQuizStrings(varCtx?.system?.locale).returnToAppFallback
          }
        />
      </a>
      {attempting && (
        <div
          data-testid={`return-to-app-opening-${block.id}`}
          style={{
            marginTop: 8,
            fontSize: 13,
            // Ревизия пачки 26 (Ф1): строка стоит на поверхности воронки
            // рядом с плашкой автора и гасла тем же способом (WEB-1911) —
            // у тёмной темы кабинета --color-text-secondary СВЕТЛАЯ.
            color: 'var(--color-text-on-surface, rgba(0, 0, 0, 0.65))',
          }}
        >
          {openingHint}
        </div>
      )}
      {showAuthorHint && (
        <AuthorHintBox
          testId={`return-to-app-link-rejected-${block.id}`}
          compact
          style={{
            marginTop: 8,
            marginBottom: 0,
          }}
        >
          {pickStrings(authorHintsStrings).returnToAppLinkNotAllowed}
        </AuthorHintBox>
      )}
    </div>
  );
}

/** Ветка `return_to_app_button` фасада — тело `case` слово в слово. */
export function renderReturnToAppButton(
  ctx: ExtendedRenderContext,
): React.ReactElement {
  const { block, varCtx, textColor, buttonStyle } = ctx;
  return (
    <ReturnToAppButton
      block={block}
      buttonStyle={buttonStyle}
      textColor={textColor}
      varCtx={varCtx}
    />
  );
}
